import { Link, useLocation } from "react-router-dom";
import { MapPin } from "lucide-react";

// Two ZenZest stores carried at all Dutchie-backed Menu pages. Kept here so
// the switcher renders without an extra API call — same source as the static
// locations list. If a third store launches, add it here.
const STORES = [
    { slug: "queens-long-island", short: "Queens", full: "Queens & Long Island" },
    { slug: "staten-island", short: "Staten Island", full: "Staten Island" },
];

// Drop-in pill switcher for any store-scoped page. Reads the current URL and
// rewrites only the store-slug segment, preserving sub-paths and querystrings
// so e.g. /menu/queens-long-island/brand/jetpacks?q=x flips cleanly to
// /menu/staten-island/brand/jetpacks?q=x without a full reset.
//
// Props:
//   currentSlug — slug of the store the user is on (drives highlight)
//   tone        — "light" (cream pills on dark bg) or "dark" (olive on light)
export default function StoreSwitcher({ currentSlug, tone = "light" }) {
    const location = useLocation();

    const rewritePath = (toSlug) => {
        const path = location.pathname;
        const search = location.search || "";
        // Match the slug that follows /menu/ or /brands/ or /locations/.
        const re = /^(\/(?:menu|brands|locations))\/[^/]+(.*)$/;
        const m = path.match(re);
        if (m) return `${m[1]}/${toSlug}${m[2]}${search}`;
        // Fallback: jump to the menu of the target store.
        return `/menu/${toSlug}${search}`;
    };

    const isDark = tone === "light"; // light tone = dark bg behind pill
    const baseCls = isDark
        ? "border-zen-cream/15 hover:border-zen-cream/35"
        : "border-zen-ink/15 hover:border-zen-olive";
    const inactiveTxt = isDark ? "text-zen-cream/70" : "text-zen-ink-soft";
    const activeCls = isDark
        ? "bg-zen-cream text-zen-ink border-zen-cream"
        : "bg-zen-ink text-zen-cream border-zen-ink";

    return (
        <div
            data-testid="store-switcher"
            className="inline-flex items-center gap-1 p-1 border border-dashed border-current/20"
            style={{ borderColor: isDark ? "rgba(245,239,225,0.18)" : "rgba(31,38,28,0.12)" }}
        >
            <span
                className={`hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] pl-2 pr-1 ${inactiveTxt}`}
            >
                <MapPin size={11} /> Switch store
            </span>
            {STORES.map((s) => {
                const active = s.slug === currentSlug;
                return (
                    <Link
                        key={s.slug}
                        to={rewritePath(s.slug)}
                        data-testid={`store-switcher-${s.slug}`}
                        aria-current={active ? "page" : undefined}
                        className={`text-[11px] uppercase tracking-[0.22em] px-3 py-1.5 border transition-colors whitespace-nowrap ${
                            active ? activeCls : `${baseCls} ${inactiveTxt} hover:text-current`
                        }`}
                    >
                        {s.short}
                    </Link>
                );
            })}
        </div>
    );
}
